import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Table, Thead, Tbody, Tr, Th, Td, Button, HStack,
  Text, Badge, IconButton, useToast, useDisclosure,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay
} from '@chakra-ui/react';
import { DeleteIcon, ViewIcon, DownloadIcon } from '@chakra-ui/icons';
import { assessmentService } from '../../services/api';

// Safe JSON parse with fallback
const safeJsonParse = (value, fallback = {}) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

export const AssessmentList = ({ assessments, onDelete, onLoad, showUser = false }) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const cancelRef = useRef();

  const openDeleteDialog = (id) => {
    setDeleteId(id);
    onOpen();
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    setIsDeleting(true);
    try {
      await assessmentService.delete(deleteId);
      onDelete(deleteId);
      toast({ title: 'Assessment deleted', status: 'success', duration: 2000 });
      onClose();
    } catch (err) {
      toast({ title: 'Delete failed', status: 'error', duration: 3000 });
    } finally {
      setIsDeleting(false);
      setDeleteId(null);
    }
  };

  // Create URL-friendly slug from name
  const slugify = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleExportPDF = (assessment) => {
    const slug = slugify(assessment.name);
    window.open(`/print/${slug}`, '_blank');
  };

  const handleExportCSV = async (id, name) => {
    try {
      const response = await assessmentService.exportCSV(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${name.replace(/[^a-z0-9]/gi, '-')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Export failed', status: 'error', duration: 3000 });
    }
  };

  if (assessments.length === 0) {
    return (
      <Box textAlign="center" py={10}>
        <Text color="gray.500">No assessments yet. Create one from the Calculator.</Text>
      </Box>
    );
  }

  return (
    <Box overflowX="auto">
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>Name</Th>
            {showUser && <Th>User</Th>}
            <Th>Complexity</Th>
            <Th>Site</Th>
            <Th>Well</Th>
            <Th>Created</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {assessments.map(assessment => {
            const scores = safeJsonParse(assessment.scores, { siteScore: 0, wellScore: 0 });
            const complexityScore = Math.round(((scores.siteScore || 0) + (scores.wellScore || 0)) / 2 * 100);

            return (
              <Tr key={assessment.id}>
                <Td
                  fontWeight="medium"
                  cursor="pointer"
                  color="blue.600"
                  _hover={{ textDecoration: 'underline' }}
                  onClick={() => onLoad(assessment)}
                >
                  {assessment.name}
                </Td>
                {showUser && (
                  <Td fontSize="sm" color="gray.600">
                    {assessment.user_email}
                  </Td>
                )}
                <Td>
                  <Badge colorScheme={complexityScore < 33 ? 'green' : complexityScore < 66 ? 'yellow' : 'red'}>
                    {complexityScore} %
                  </Badge>
                </Td>
                <Td>
                  <Badge colorScheme="red">{Math.round(scores.siteScore * 100)} %</Badge>
                </Td>
                <Td>
                  <Badge colorScheme="blue">{Math.round(scores.wellScore * 100)} %</Badge>
                </Td>
                <Td fontSize="sm" color="gray.600">
                  {new Date(assessment.created_at).toLocaleDateString()}
                </Td>
                <Td>
                  <HStack spacing={1}>
                    <IconButton
                      icon={<ViewIcon />}
                      size="sm"
                      variant="ghost"
                      aria-label="View"
                      onClick={() => onLoad(assessment)}
                    />
                    <IconButton
                      icon={<DownloadIcon />}
                      size="sm"
                      variant="ghost"
                      aria-label="Export PDF"
                      onClick={() => handleExportPDF(assessment)}
                    />
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => handleExportCSV(assessment.id, assessment.name)}
                    >
                      CSV
                    </Button>
                    <IconButton
                      icon={<DeleteIcon />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      aria-label="Delete"
                      onClick={() => openDeleteDialog(assessment.id)}
                    />
                  </HStack>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Assessment
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this assessment? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} isDisabled={isDeleting}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDelete}
                ml={3}
                isLoading={isDeleting}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

AssessmentList.propTypes = {
  assessments: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    scores: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    created_at: PropTypes.string,
    user_email: PropTypes.string
  })).isRequired,
  onDelete: PropTypes.func.isRequired,
  onLoad: PropTypes.func.isRequired,
  showUser: PropTypes.bool
};

export default AssessmentList;
